import React, { ReactElement, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Link } from 'react-router-dom';
import { Button, Col, Container, Row } from 'reactstrap';
import { ApplicationRoutes, AppSettings, FeatureFlags, ProductNames } from '../../../constants';
import useNavigationService from '../../../hooks/UseNavigationService';
import { HomeApiService } from '../../../services/api/HomeApiService';
import { useGeneralContext } from '../../../services/GeneralContext';
import { DisclaimerRow } from '../../DisclaimerRow/DisclaimerRow';
import DateHelper from '../../Helpers/DateHelper';
import GeneralHelper from '../../Helpers/GeneralHelper';
import { FAQs } from '../../HomeElements/FAQs';
import NectarineButton from '../../ui/NectarineButton/NectarineButton';
import heroImage from './../../../img/investing-experts.png';
import reviewUser3 from './../../../img/testimonials/review-johnmichael.jpg';
import reviewUser1 from './../../../img/testimonials/review-julie.jpg';
import reviewUser2 from './../../../img/testimonials/review-rina.jpg';
import './../../../styles/common.scss';
import './../Home/Home.scss';

interface LandingPageTemplateProps {
  singular: string;
  plural: string;
  title?: string;
  disclaimer?: string;
  blurb?: ReactElement;
}

const howWeCanHelpOptions: { text: string; iconCSSClass: string }[] = [
  {
    text: 'Retirement planning',
    iconCSSClass: 'fa-duotone fa-solid fa-island-tropical'
  },
  {
    text: 'Portfolio review',
    iconCSSClass: 'fa-duotone fa-solid fa-chart-pie'
  },
  {
    text: 'Estate planning',
    iconCSSClass: 'fa-duotone fa-solid fa-people-roof'
  },
  {
    text: '401k rollovers',
    iconCSSClass: 'fa-duotone fa-solid fa-money-bill-transfer'
  },
  {
    text: 'Tax planning',
    iconCSSClass: 'fa-duotone fa-solid fa-file-invoice-dollar'
  },
  {
    text: 'Getting started with investing',
    iconCSSClass: 'fa-duotone fa-solid fa-display-chart-up-circle-dollar'
  },
  {
    text: 'Investing for kids',
    iconCSSClass: 'fa-duotone fa-solid fa-baby-carriage'
  },
  {
    text: '...and more!',
    iconCSSClass: 'fa-duotone fa-solid fa-circle-plus'
  }
];

const ourPhilosophyOptions: { text: string; iconCSSClass: string }[] = [
  { text: 'Buy & hold index funds', iconCSSClass: 'fa-duotone fa-solid fa-money-bill-trend-up' },
  { text: 'Minimize fees', iconCSSClass: 'fa-duotone fa-solid fa-cash-register' },
  { text: 'Minimize taxes', iconCSSClass: 'fa-duotone fa-solid fa-list-check' },
  { text: 'Keep it simple', iconCSSClass: 'fa-duotone fa-solid fa-circle-check' },
  { text: 'Do the right thing', iconCSSClass: 'fa-duotone fa-solid fa-scale-balanced' }
];

const LandingPageTemplate: React.FC<LandingPageTemplateProps> = (props: LandingPageTemplateProps) => {
  const { navigateTo_AdvisorsResults } = useNavigationService();

  const [dashboardData, setDashboardData] = useState<Home_DashboardPage_ViewModel | null>(null);

  const { checkFeatureFlag } = useGeneralContext();

  const showTheOngoingFields = checkFeatureFlag(FeatureFlags.TheOngoing);

  const now = new Date();

  useEffect(() => {
    const loadData = async () => {
      const dashboardData = await HomeApiService.getDashboardData();
      setDashboardData(dashboardData);
    };

    loadData();
  }, []);

  function navigateToAdvisorsResults() {
    navigateTo_AdvisorsResults();
  }

  function printPriceRange(product: 'the-hour' | 'the-plan' | 'the-ongoing') {
    if (!dashboardData) {
      return <Skeleton width={80} inline />;
    }

    if (product === 'the-hour') {
      return GeneralHelper.formatPriceRange(dashboardData.priceRange_TheHour.low, dashboardData.priceRange_TheHour.high);
    } else if (product === 'the-plan') {
      return GeneralHelper.formatPriceRange(dashboardData.priceRange_ThePlan.low, dashboardData.priceRange_ThePlan.high);
    } else if (product === 'the-ongoing') {
      return GeneralHelper.formatPriceRange(dashboardData.priceRange_TheOngoing.low, dashboardData.priceRange_TheOngoing.high);
    }
  }

  return (
    <Container className="home-page">
      <Helmet>
        <title>
          {props.title ?? 'Flat-Fee Hourly Fiduciary Financial Advisors'} - {AppSettings.ApplicationName}
        </title>
      </Helmet>

      <Row className="hero-row" md="2" sm="1" xs="1">
        <Col>
          <h1 id="home">
            {props.title || 'Just Financial Advice'}
            <span className="light-blue">.</span>
          </h1>
          <h3>We don't manage your money, sell any products, or earn any commissions. Pay for just the advice.</h3>
          <div className="cta">
            <p>
              From
              <strong> {printPriceRange('the-hour')}</strong>
              /hour, <strong className="green">no strings attached.</strong>
            </p>
            <Button color="primary" size="lg" className="search-button" onClick={navigateToAdvisorsResults}>
              Find your {props.singular}
            </Button>
          </div>
        </Col>
        <Col className="text-center">
          <img src={heroImage} className="hero" alt="Nectarine Financial Advisors Illustration" />
        </Col>
      </Row>

      {props.disclaimer ? (
        <Row>
          <Col className="affiliate-disclaimer">{props.disclaimer}</Col>
        </Row>
      ) : (
        <></>
      )}

      <Row className="home-section-head">
        <h2 id="howwecanhelp">How we can help</h2>
        <p>Meet with an expert US-based {props.singular} and get advice on various areas of finance and investing.</p>
      </Row>

      <Row>
        <Col xs="0" md="1"></Col>
        <Col>
          <Row className="how-we-can-help gy-5 mx-lg-5">
            {howWeCanHelpOptions.map((v) => {
              return (
                <Col key={v.text} xs="6" md="3">
                  <i className={v.iconCSSClass}></i>
                  <p>{v.text}</p>
                </Col>
              );
            })}
          </Row>
        </Col>
        <Col xs="0" md="1"></Col>
      </Row>

      <Row className="home-section-head">
        <h2 id="experts">Our expert {props.plural}</h2>
        <p>
          All Nectarine {props.plural} are licensed fiduciaries who have been vetted for their stellar knowledge, competency, and investment
          philosophy. We have accepted <strong>less than 10%</strong> of the {props.plural} who have applied to join Nectarine!
        </p>
      </Row>

      {!dashboardData ? (
        <Row className="our-experts g-4 mb-4" md="3" sm="1" xs="1">
          <Col>
            <Skeleton width={400} height={580} />
          </Col>
          <Col>
            <Skeleton width={400} height={580} />
          </Col>
          <Col>
            <Skeleton width={400} height={580} />
          </Col>
        </Row>
      ) : (
        <></>
      )}

      {dashboardData ? (
        <Row className="our-experts g-4" md="3" sm="1" xs="1">
          {dashboardData.advisors.map((advisor) => (
            <Col key={advisor.id}>
              <div className="advisor-home-cell d-flex flex-column h-100">
                <div className="advisor-home-image">
                  <Link to={`/${ApplicationRoutes.Advisor}/${advisor.slug}`}>
                    {advisor.profilePicUrl ? (
                      <img
                        src={advisor.profilePicUrl}
                        className="circle-image headshot"
                        alt={advisor.firstName + ' ' + advisor.lastName + ', ' + advisor.certifications}
                      />
                    ) : (
                      <span className="fa-duotone fa-solid fa-circle-user font-rem-10 c-blue"></span>
                    )}
                  </Link>
                </div>
                <h1 className="results-name">
                  <Link to={`/${ApplicationRoutes.Advisor}/${advisor.slug}`}>
                    {advisor.firstName} {advisor.lastName}
                    {advisor.certifications !== null && advisor.certifications !== undefined && advisor.certifications !== '' ? ', ' : ''}
                    {advisor.certifications}
                  </Link>
                </h1>
                <Row>
                  <Col>
                    <h2>{advisor.title}</h2>
                  </Col>
                  <Col className="text-end">
                    <div className="results-reviews">
                      {advisor.reviewAverage > 0 && (
                        <span id="AverageReviewTooltip">
                          <i className="fa-star fa-solid"></i> {advisor.reviewAverage.toFixed(2)} ({advisor.reviewCount})
                        </span>
                      )}
                    </div>
                  </Col>
                </Row>

                <ul>
                  {(advisor.bio ?? '').split('\n').map((item, key) => (
                    <li key={key}>{item}</li>
                  ))}
                </ul>
                <div className="mt-auto">
                  <div className="text-center">
                    <span className="bold font-l2">${Math.round(advisor.hourlyRate).toLocaleString('en-US')}</span>
                    <span className="results-product"> / one hour</span>
                  </div>
                  <div className="text-center">
                    <Link to={`/${ApplicationRoutes.Advisor}/${advisor.slug}`} className="btn btn-light results-btn">
                      <span className="view-profile">View Profile</span>

                      {advisor.nextAvailability ? (
                        new Date(advisor.nextAvailability) > now ? (
                          <span className="next-available">
                            <br />
                            Next Available: {DateHelper.casualDateFormat(advisor.nextAvailability)}
                          </span>
                        ) : (
                          <></>
                        )
                      ) : (
                        <></>
                      )}
                    </Link>
                  </div>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      ) : (
        <></>
      )}

      <Row className="text-center mt-4">
        <Col>
          <NectarineButton text={`View all ${props.plural}`} onClick={() => navigateTo_AdvisorsResults()} size="smedium" />
        </Col>
      </Row>

      <Row className="home-section-head">
        <h2 id="howitworks">How it works</h2>
      </Row>

      <Row className="how-it-works mx-lg-5">
        <Col xs="0" md="1"></Col>

        <Col>
          <div className="how-it-works-step">
            <i className="fa-solid fa-arrow-pointer hiw-icon"></i>
            <h3>1. Select</h3>
            <p>Choose from a list of vetted & licensed {props.plural}.</p>
          </div>
        </Col>
        <Col>
          <div className="how-it-works-step">
            <i className="fa-duotone fa-solid fa-calendar-lines-pen hiw-icon"></i>
            <h3>2. Schedule</h3>
            <p>Pick a time to meet with your {props.singular} and pay for the session.</p>
          </div>
        </Col>
        <Col>
          <div className="how-it-works-step">
            <i className="fa-duotone fa-solid fa-comments hiw-icon"></i>
            <h3>3. Get advice</h3>
            <p>Video chat, share your screen, and get your questions answered.</p>
          </div>
        </Col>

        <Col xs="0" md="1"></Col>
      </Row>

      {props.blurb ? (
        <Row>
          <h2>Why {props.title || 'Advice-Only Financial Advisors'}</h2>
          <div className="text-start">{props.blurb}</div>
        </Row>
      ) : (
        <></>
      )}

      <Row className="home-section-head">
        <h2 id="howwecanhelp">What they're saying</h2>
        {dashboardData ? (
          <p>
            Nectarine advisors have served <strong className="light-blue">{dashboardData.meetingsCompleted.toLocaleString()}</strong>{' '}
            clients and have received an average review of <strong className="light-blue">{dashboardData.averageReview.toFixed(2)}</strong>{' '}
            stars!
          </p>
        ) : (
          <></>
        )}

        {!dashboardData ? (
          <p>
            <Skeleton count={0.75} />
          </p>
        ) : (
          <></>
        )}

        <div className="home-stars">
          {Array.from(Array(5)).map((v, i) => (
            <i key={i} className="fa-solid fa-star"></i>
          ))}
        </div>
      </Row>
      <Row>
        {testimonialData.map((testimonial) => (
          <Col lg="4" md="12" className="mb-4" key={testimonial.slug}>
            <div className="testimonial d-flex flex-column h-100">
              <div className="testimonial-stars">
                {Array.from(Array(5)).map((v, i) => (
                  <i key={i} className="fa-solid fa-star"></i>
                ))}
              </div>

              <div className="mt-3"></div>

              <div className="testimonialText">{testimonial.text}</div>

              <div className="mt-3"></div>

              {!dashboardData ? (
                <div className="testimonial-pics mb-3">
                  <Skeleton width={90} height={90} circle={true} inline={true} />
                  <Skeleton width={90} height={90} circle={true} inline={true} />
                </div>
              ) : (
                <></>
              )}

              {dashboardData ? (
                <div className="testimonial-pics mb-3">
                  <img src={testimonial.clientImageUrl} className="circle-image headshot" />

                  <Link to={`/${ApplicationRoutes.Advisor}/${testimonial.slug}`}>
                    <img src={dashboardData.profilePics[testimonial.slug]} className="circle-image headshot" />
                  </Link>
                </div>
              ) : (
                <></>
              )}

              <div className="mb-3">
                <strong>{testimonial.client}</strong> met with{' '}
                <Link to={`/${ApplicationRoutes.Advisor}/${testimonial.slug}`}>{testimonial.advisor}</Link>
                <div className="review-date">{DateHelper.printMonthYearLong(testimonial.date)}</div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      <DisclaimerRow />

      <Row className="home-section-head">
        <h2 id="howwecanhelp">Our Philosophy</h2>
        <p>
          We never sell any products or manage your money so our only incentive is doing the <strong>best for you</strong>.
        </p>
      </Row>
      <Row className="how-we-can-help g-4">
        <Col xs="0" md="1"></Col>

        {ourPhilosophyOptions.map((v) => {
          return (
            <Col key={v.text}>
              <i className={v.iconCSSClass}></i>
              <p>{v.text}</p>
            </Col>
          );
        })}

        <Col xs="0" md="1"></Col>
      </Row>

      <Row className="home-section-head">
        <h2 id="pricing">Services &amp; Pricing</h2>
      </Row>

      <Row>
        {!showTheOngoingFields ? <Col xs="2"></Col> : <></>}

        <Col xs="12" xl="4">
          <div className="price-box">
            <div className="price-header">
              <h3>{ProductNames.TheHour}</h3>
            </div>

            <div className="price-details">
              <div className="text-start">A one hour session to talk with a financial advisor</div>

              <div className="topSection text-start mt-4">
                <div>Includes:</div>
                <ul>
                  <li>1 hour meeting</li>
                  <li>Follow up notes</li>
                  <li className="opacity-0">&nbsp;</li>
                </ul>

                <p className="text-start mt-3">Best for answering 1-3 specific questions about your finances.</p>
              </div>

              <h2 className="d-flex flex-nowrap aie jcc gap-2">
                <span className="price">{printPriceRange('the-hour')}</span>
                <span className="hour font-l mb-2">/ meeting</span>
              </h2>

              <div className="c-gray font-m mb-3">one time</div>

              {/* NOTE: This button is going away when we add the Ongoing product, but show it for now until that's live  */}
              {!showTheOngoingFields ? (
                <NectarineButton text={`Find your ${props.singular}`} size="smedium" onClick={navigateToAdvisorsResults} />
              ) : (
                <></>
              )}
            </div>
          </div>
        </Col>
        <Col xs="12" xl="4">
          <div className="price-box">
            <div className="price-header">
              <h3>{ProductNames.ThePlan}</h3>
            </div>

            <div className="price-details">
              <div className="text-start">An 8 week comprehensive program to map out your financial future</div>

              <div className="topSection text-start mt-4">
                <div>Includes:</div>
                <ul>
                  <li>3-4 meetings</li>
                  <li>8+ page financial plan</li>
                  <li>28+ hours of analysis</li>
                </ul>

                <p className="text-start mt-3">
                  Best for a one time full review of your finances and creating a strategic plan for your future.
                </p>
              </div>

              <h2 className="d-flex flex-nowrap aie jcc gap-2">
                <span className="price">{printPriceRange('the-plan')}</span>

                <span className="hour font-l mb-2">/ plan</span>
              </h2>

              <div className="c-gray font-m mb-3">one time</div>

              {/* NOTE: This button is going away when we add the Ongoing product, but show it for now until that's live  */}
              {!showTheOngoingFields ? (
                <NectarineButton text="Learn More" size="smedium" href={`/${ApplicationRoutes.ThePlan}`} hrefOpensNewTab={false} />
              ) : (
                <></>
              )}
            </div>
          </div>
        </Col>

        {showTheOngoingFields ? (
          <Col xs="12" xl="4">
            <div className="price-box">
              <div className="price-header">
                <h3>{ProductNames.TheOngoing}</h3>
              </div>

              <div className="price-details">
                <div className="text-start">An ongoing relationship with a dedicated financial advisor</div>

                <div className="topSection text-start mt-4">
                  <div>Includes:</div>
                  <ul>
                    <li>Regular check ins & reminders</li>
                    <li>Unlimited email support</li>
                    <li>Annual 2 page financial checkup</li>
                  </ul>

                  <p className="text-start mt-3">
                    Best for an ongoing relationship with an advisor who's there to answer your questions and keep you on track.
                  </p>
                </div>

                <h2 className="d-flex flex-nowrap aie jcc gap-2">
                  <span className="price">{printPriceRange('the-ongoing')}</span>

                  <span className="hour font-l mb-2"> / month</span>
                </h2>

                <div className="c-gray font-m mb-3">billed every three months</div>
              </div>
            </div>
          </Col>
        ) : (
          <></>
        )}

        {!showTheOngoingFields ? <Col xs="2"></Col> : <></>}
      </Row>

      {showTheOngoingFields ? (
        <div className="d-flex jcc aic gap-4 mt-4">
          <NectarineButton text="Choose your advisor" size="smedium" href={`/${ApplicationRoutes.Advisors}`} hrefOpensNewTab={false} />

          <NectarineButton
            text="Compare all services"
            size="smedium"
            color="white"
            href={`/${ApplicationRoutes.Services}`}
            hrefOpensNewTab={false}
          />
        </div>
      ) : (
        <></>
      )}

      <Row className="home-section-head">
        <h2>Our promise</h2>
        <p>Nectarine is committed to doing the right thing and prioritizing your best interests.</p>
      </Row>

      <Row className="how-it-works">
        <Col xs="0" md="1"></Col>

        <Col xs="12" md className="mb-4">
          <div className="how-it-works-step">
            <i className="fa-duotone fa-badge-check hiw-icon"></i>
            <h3>Our pledge</h3>
            <p>
              If you're not satisfied with your meeting, we'll give you <b>your money back</b>.
            </p>
          </div>
        </Col>

        <Col xs="12" md className="mb-4">
          <div className="how-it-works-step">
            <i className="fa-duotone fa-solid fa-calendar hiw-icon"></i>
            <h3>Flexible scheduling</h3>
            <p>
              <b>Free</b> reschedules &amp; cancellations with 24 hours notice.
            </p>
          </div>
        </Col>

        <Col xs="12" md className="mb-4">
          <div className="how-it-works-step">
            <i className="fa-duotone fa-solid fa-person-chalkboard hiw-icon"></i>
            <h3>No sales pitch</h3>
            <p>
              We'll <b>never pitch</b> any investment or insurance products.
            </p>
          </div>
        </Col>

        <Col xs="0" md="1"></Col>
      </Row>

      <Row className="home-section-head">
        <h2>Frequently Asked Questions</h2>
      </Row>
      <FAQs />

      <Row className="center last-button">
        <div>
          <NectarineButton text={`Find your ${props.singular}`} size="smedium" onClick={navigateToAdvisorsResults} />
        </div>

        <div className="mt-3">
          <Link to={`/${ApplicationRoutes.NonUS}`} className="cta-link">
            Live outside the US?
          </Link>
        </div>
      </Row>
    </Container>
  );
};

const testimonialData: { slug: string; client: string; advisor: string; clientImageUrl: string; date: Date; text: ReactElement }[] = [
  {
    slug: 'tommy-modec',
    client: 'Julie R.',
    advisor: 'Tommy Modec',
    clientImageUrl: reviewUser1,
    date: new Date('2024-03-25T00:00:00.000Z'),
    text: (
      <p>
        Tommy is our "guy" now, <b>we won't be looking for advice from anyone else</b> going forward. If you're on the fence about booking a
        meeting, just do it. Nectarine is seriously the BEST!
      </p>
    )
  },
  {
    slug: 'kelly-palmer',
    client: 'Rina S.',
    advisor: 'Kelly Palmer',
    clientImageUrl: reviewUser2,
    date: new Date('2024-07-05T00:00:00.000Z'),
    text: (
      <p>
        Kelly doesn't just provide advice; she <b>empowers me with the knowledge and tools</b> needed to make informed financial decisions.
        Her belief in her clients' potential, coupled with her practical and compassionate approach, makes her an exceptional advisor.
      </p>
    )
  },
  {
    slug: 'sara-young',
    client: 'John Michael D.',
    advisor: 'Sara Young',
    clientImageUrl: reviewUser3,
    date: new Date('2024-05-05T00:00:00.000Z'),
    text: (
      <p>
        Sara was awesome. At the top of the meeting I shared what I hoped to get and by the end of the meeting Sara had hit everything! I
        walked out with a <b>to-do list of items I'm motivated to check off</b>. Would certainly book again.
      </p>
    )
  }
];

export default LandingPageTemplate;
